import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { authClient } from "@/lib/auth";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import Button from "@/components/ui/Button";
import TextInput from "@/components/ui/TextInput";
import { AuthShell } from "./ForgotPassword";

export function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const { error } = await authClient.resetPassword({ newPassword: password, token });
      if (error) {
        setErr(error.message ?? "Could not reset password. The link may have expired.");
      } else {
        nav("/login?reset=1");
      }
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Unknown error");
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthShell title="Invalid link" eyebrow="Members">
        <p
          className="text-center text-sm"
          style={{ fontFamily: fonts.body, color: `${colors.navy}aa`, lineHeight: 1.6 }}
        >
          This password reset link is missing or invalid. Request a new one.
        </p>
        <div className="mt-5 text-center">
          <Link
            to="/forgot-password"
            className="text-sm underline transition-opacity hover:opacity-70"
            style={{ fontFamily: fonts.body, fontWeight: 600, color: colors.berryRose, letterSpacing: "0.03em" }}
          >
            Request a reset link
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password" eyebrow="Members">
      <form onSubmit={submit} className="grid gap-4">
        <TextInput
          label="New password"
          name="password"
          type="password"
          minLength={8}
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <TextInput
          label="Confirm new password"
          name="confirm"
          type="password"
          minLength={8}
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
        />
        {err && (
          <p
            className="text-center text-sm"
            style={{ color: colors.berryRose, fontFamily: fonts.body, fontWeight: 600 }}
          >
            {err}
          </p>
        )}
        <Button type="submit" className="mt-1 w-full" disabled={busy}>
          {busy ? "…" : "Reset password"}
        </Button>
      </form>
      <div className="mt-5 text-center">
        <Link
          to="/login"
          className="text-sm underline transition-opacity hover:opacity-70"
          style={{ fontFamily: fonts.body, fontWeight: 600, color: colors.berryRose, letterSpacing: "0.03em" }}
        >
          Back to sign in
        </Link>
      </div>
    </AuthShell>
  );
}
